import React, { useState } from 'react';
import { Check, Zap, Server, Shield, Globe, ArrowRight, HelpCircle, Sparkles } from 'lucide-react';
import { HOSTING_PLANS, HostingPlan } from '../data/siteData';

interface HostingPricingSectionProps {
  onOrderPlan: (plan: HostingPlan, billingCycle: 'annual' | 'biennial') => void;
  onOpenBooking: () => void;
  asPageHeader?: boolean;
}

export const HostingPricingSection: React.FC<HostingPricingSectionProps> = ({ onOrderPlan, onOpenBooking, asPageHeader = true }) => {
  const [billingCycle, setBillingCycle] = useState<'annual' | 'biennial'>('annual');

  return (
    <section id="hosting" className="py-20 lg:py-28 bg-slate-50 border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="max-w-3xl text-left mb-12">
          <div className="text-xs font-mono font-bold uppercase tracking-widest text-[#b88e4c] mb-2">
            Hosting &amp; Cloud
          </div>
          {asPageHeader ? (
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-serif-heading">
              Web hosting in Kenya, engineered <br className="hidden sm:block" />
              for serious businesses.
            </h1>
          ) : (
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-serif-heading">
              Web hosting in Kenya, engineered <br className="hidden sm:block" />
              for serious businesses.
            </h2>
          )}
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
            NVMe SSD LiteSpeed hosting from <strong className="text-slate-900 font-bold">KSh 3,875 / year</strong> with a free domain, free SSL, unlimited email, free M-Pesa integration and 24/7 Nairobi-based support.
          </p>

          {/* Billing Cycle Toggle */}
          <div className="mt-6 inline-flex items-center p-1 bg-slate-200/80 rounded-xl text-xs font-semibold">
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                billingCycle === 'annual'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              1 Year (Standard)
            </button>
            <button
              onClick={() => setBillingCycle('biennial')}
              className={`px-4 py-2 rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 ${
                billingCycle === 'biennial'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span>2 Years</span>
              <span className="text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.2 rounded font-bold">
                Save 15%
              </span>
            </button>
          </div>
        </div>

        {/* 4 Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {HOSTING_PLANS.map((plan) => {
            const isPopular = plan.badge === 'Most popular';
            const price = billingCycle === 'annual' ? plan.priceYear : Math.round(plan.priceYear * 2 * 0.85);
            const periodLabel = billingCycle === 'annual' ? '/ year excl. VAT' : '/ 2 years excl. VAT';

            return (
              <div
                key={plan.id}
                className={`flex flex-col justify-between rounded-2xl p-6 sm:p-7 text-left transition-all duration-200 relative ${
                  isPopular
                    ? 'bg-white border-2 border-[#b88e4c] shadow-xl ring-2 ring-emerald-500/10'
                    : 'bg-white border border-slate-200 shadow-xs hover:border-slate-300'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#b88e4c] text-slate-950 font-extrabold text-xs tracking-wider uppercase px-3 py-1 rounded-full shadow-sm">
                    Most Popular
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-slate-900 font-display">
                      {plan.name}
                    </h3>
                  </div>

                  <p className="text-xs text-slate-500 mb-4 min-h-[32px]">
                    {plan.targetAudience}
                  </p>

                  <div className="mb-6 pb-6 border-b border-slate-100">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs font-semibold text-slate-500">KSh</span>
                      <span className="text-3xl font-extrabold text-slate-900 font-display tabular-nums">
                        {price.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {periodLabel}
                    </div>
                  </div>

                  {/* Core Specs Quick Badges */}
                  <div className="space-y-1.5 mb-6 text-xs text-slate-700 font-medium">
                    <div className="flex items-center justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">Storage</span>
                      <span className="font-semibold text-slate-900">{plan.specs.disk}</span>
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">Memory</span>
                      <span className="font-semibold text-slate-900">{plan.specs.ram}</span>
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">Free Domain</span>
                      <span className="font-semibold text-slate-900 text-right">{plan.specs.domain.includes('FREE') ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex items-center justify-between py-1 border-b border-slate-50">
                      <span className="text-slate-500">SSL Certificate</span>
                      <span className="text-[#b88e4c] font-semibold">Included</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <span className="text-slate-500">M-Pesa Hook</span>
                      <span className="text-[#b88e4c] font-semibold">Ready</span>
                    </div>
                  </div>

                  {/* Full Features List */}
                  <div className="space-y-2 mb-8">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Included in package:
                    </div>
                    {plan.features.slice(0, 6).map((feat) => (
                      <div key={feat} className="flex items-start gap-2 text-xs text-slate-600">
                        <Check className="h-3.5 w-3.5 text-[#b88e4c] shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <button
                    onClick={() => onOrderPlan(plan, billingCycle)}
                    className={`w-full py-3 px-4 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                      isPopular
                        ? 'bg-[#b88e4c] hover:bg-[#009fe3] text-slate-950 shadow-md'
                        : 'bg-[#080d11] hover:bg-slate-800 text-white'
                    }`}
                  >
                    <span>Order {plan.name}</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                  <p className="text-xs text-center text-slate-400 mt-2">
                    Instant automated provisioning
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Technical Hosting Features Banner */}
        <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-12 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <div className="h-10 w-10 rounded-xl bg-[#b88e4c]/20 text-[#009fe3] flex items-center justify-center mb-4">
                <Zap className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold font-display text-white mb-2">
                LiteSpeed + NVMe Performance
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Up to 20x faster than traditional Apache or standard SATA HDD hosting. Instant response times optimized for Kenyan mobile networks.
              </p>
            </div>

            <div>
              <div className="h-10 w-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center mb-4">
                <Server className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold font-display text-white mb-2">
                East Africa Edge Proximity
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Direct peering with Kenya Internet Exchange Point (KIXP), Safaricom, and Airtel networks ensures single-digit millisecond latency.
              </p>
            </div>

            <div>
              <div className="h-10 w-10 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center mb-4">
                <Shield className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold font-display text-white mb-2">
                Imunify360 + Daily Backups
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Automated malware detection, proactive DDoS protection, and off-site daily snapshots that can be restored with a single click.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
